import { useState, useEffect } from 'react';
import { settingsAPI, purchaseOrdersAPI } from '../api/client';
import apiClient from '../api/client';

export default function Settings() {
  const [settings, setSettings] = useState({
    azureAdTenantId: '',
    azureAdClientId: '',
    azureAdClientSecret: '',
    ssoEnabled: false,
    allowLocalLogin: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Purchase Order settings state
  const [poSettings, setPoSettings] = useState({ mode: null, enforcement: 'warn', tolerancePct: 0 });
  const [poSaving, setPoSaving] = useState(false);
  const [poSyncing, setPoSyncing] = useState(false);
  const [poMessage, setPoMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    loadSettings();
    loadPOSettings();
  }, []);

  const loadPOSettings = async () => {
    try {
      const r = await purchaseOrdersAPI.getSettings();
      setPoSettings(r.data);
    } catch { /* module may not be installed yet */ }
  };

  const handlePOSave = async () => {
    setPoSaving(true);
    setPoMessage({ type: '', text: '' });
    try {
      await purchaseOrdersAPI.updateSettings(poSettings);
      setPoMessage({ type: 'success', text: 'Purchase Order settings saved.' });
    } catch (err) {
      setPoMessage({ type: 'error', text: err.response?.data?.message || 'Failed to save PO settings' });
    } finally {
      setPoSaving(false);
    }
  };

  const handleERPSync = async () => {
    setPoSyncing(true);
    setPoMessage({ type: '', text: '' });
    try {
      const r = await purchaseOrdersAPI.erpSync();
      setPoMessage({ type: 'success', text: `ERP sync complete: ${r.data.created} created, ${r.data.updated} updated${r.data.errors?.length ? `, ${r.data.errors.length} error(s)` : ''}.` });
    } catch (err) {
      setPoMessage({ type: 'error', text: err.response?.data?.message || 'ERP sync failed' });
    } finally {
      setPoSyncing(false);
    }
  };

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await settingsAPI.getSSO();
      setSettings(response.data);
    } catch (err) {
      console.error('Failed to load settings:', err);
      setMessage({ type: 'error', text: 'Failed to load SSO settings' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });
      await settingsAPI.updateSSO(settings);
      setMessage({ type: 'success', text: 'SSO settings saved successfully!' });
    } catch (err) {
      console.error('Failed to save settings:', err);
      setMessage({ type: 'error', text: err.response?.data?.message || 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      setSaving(true);
      setMessage({ type: '', text: '' });
      const response = await settingsAPI.testSSO({
        azureAdTenantId: settings.azureAdTenantId,
        azureAdClientId: settings.azureAdClientId,
        azureAdClientSecret: settings.azureAdClientSecret
      });
      setMessage({ type: 'success', text: 'Azure AD connection successful! ' + response.data.message });
    } catch (err) {
      console.error('Connection test failed:', err);
      setMessage({ type: 'error', text: err.response?.data?.message || 'Connection test failed' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">System Settings</h1>
        <p className="text-gray-600 mt-1">Configure Azure AD SSO and authentication settings</p>
      </div>

      {message.text && (
        <div className={`mb-4 px-4 py-3 rounded-lg ${
          message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-6">
        {/* Azure AD Configuration */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Azure AD Configuration</h2>
          
          <div className="space-y-4">
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="ssoEnabled"
                  checked={settings.ssoEnabled}
                  onChange={handleInputChange}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm font-medium text-gray-700">Enable Azure AD SSO</span>
              </label>
              <p className="mt-1 text-sm text-gray-500">Allow users to sign in using their Azure AD credentials</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Azure AD Tenant ID *
              </label>
              <input
                type="text"
                name="azureAdTenantId"
                value={settings.azureAdTenantId}
                onChange={handleInputChange}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required={settings.ssoEnabled}
              />
              <p className="mt-1 text-sm text-gray-500">Found in Azure Portal → Azure Active Directory → Overview</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Application (Client) ID *
              </label>
              <input
                type="text"
                name="azureAdClientId"
                value={settings.azureAdClientId}
                onChange={handleInputChange}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required={settings.ssoEnabled}
              />
              <p className="mt-1 text-sm text-gray-500">Found in App registrations → Your App → Overview</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Client Secret *
              </label>
              <input
                type="password"
                name="azureAdClientSecret"
                value={settings.azureAdClientSecret}
                onChange={handleInputChange}
                placeholder="Enter client secret"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required={settings.ssoEnabled}
              />
              <p className="mt-1 text-sm text-gray-500">Found in App registrations → Your App → Certificates & secrets</p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-sm font-medium text-blue-900 mb-2">Setup Instructions:</h3>
              <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                <li>Go to Azure Portal → Azure Active Directory → App registrations</li>
                <li>Create a new registration or select existing app</li>
                <li>Add Redirect URI: <code className="bg-blue-100 px-1 rounded">http://localhost:3000/auth/callback</code></li>
                <li>Generate a client secret under "Certificates & secrets"</li>
                <li>Copy Tenant ID, Client ID, and Client Secret to the fields above</li>
              </ol>
            </div>
          </div>
        </div>

        {/* Authentication Options */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Authentication Options</h2>
          
          <div className="space-y-4">
            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="allowLocalLogin"
                  checked={settings.allowLocalLogin}
                  onChange={handleInputChange}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm font-medium text-gray-700">Allow local username/password login</span>
              </label>
              <p className="mt-1 text-sm text-gray-500">
                Keep enabled to allow fallback login with username/password. 
                Disable this after SSO is fully configured and tested.
              </p>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800">
                <strong>⚠️ Important:</strong> Do not disable local login until you have successfully logged in via Azure AD 
                and created an SSO super admin user. Keep the default superadmin account enabled as a backup until SSO is verified.
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={handleTestConnection}
            disabled={saving || !settings.ssoEnabled}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Test Connection
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>

      {/* User Management Link */}
      <div className="mt-8 bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Purchase Orders</h2>
        <p className="text-gray-500 text-sm mb-4">Configure the Purchase Order module for your company.</p>

        {poMessage.text && (
          <div className={`mb-4 px-4 py-3 rounded-lg text-sm ${
            poMessage.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
          }`}>{poMessage.text}</div>
        )}

        <div className="space-y-4">
          {/* Mode */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">PO Mode</label>
            <select
              value={poSettings.mode || ''}
              onChange={e => setPoSettings(s => ({ ...s, mode: e.target.value || null }))}
              className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Disabled (plain text field)</option>
              <option value="internal">Internal — SpocoDoc manages POs</option>
              <option value="erp_sync">ERP Sync — read-only from Dynamics BC / Xero</option>
              <option value="both">Both — internal + ERP sync</option>
            </select>
          </div>

          {poSettings.mode && (
            <>
              {/* Enforcement */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Enforcement</label>
                <select
                  value={poSettings.enforcement || 'warn'}
                  onChange={e => setPoSettings(s => ({ ...s, enforcement: e.target.value }))}
                  className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="warn">Warn — show warning but allow submission</option>
                  <option value="block">Block — prevent submission without a PO</option>
                </select>
              </div>

              {/* Tolerance */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Invoice tolerance (%)</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number" min="0" max="100" step="0.5"
                    value={poSettings.tolerancePct || 0}
                    onChange={e => setPoSettings(s => ({ ...s, tolerancePct: parseFloat(e.target.value) || 0 }))}
                    className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-gray-500">% above PO total allowed when matching invoices</span>
                </div>
              </div>

              {/* ERP sync button */}
              {(poSettings.mode === 'erp_sync' || poSettings.mode === 'both') && (
                <div>
                  <button
                    type="button"
                    onClick={handleERPSync}
                    disabled={poSyncing}
                    className="px-4 py-2 border border-purple-300 text-purple-700 rounded-lg text-sm hover:bg-purple-50 disabled:opacity-50"
                  >
                    {poSyncing ? 'Syncing…' : 'Sync POs from ERP now'}
                  </button>
                  <p className="text-xs text-gray-400 mt-1">Reads purchase orders from Dynamics BC / Xero using your Finance Integration credentials</p>
                </div>
              )}
            </>
          )}

          <div className="pt-2">
            <button
              type="button"
              onClick={handlePOSave}
              disabled={poSaving}
              className="px-5 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 disabled:opacity-50 font-medium"
            >
              {poSaving ? 'Saving…' : 'Save PO Settings'}
            </button>
          </div>
        </div>
      </div>

      {/* User Management Link */}
      <div className="mt-8 bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-2">User Management</h2>
        <p className="text-gray-600 mb-4">
          After setting up SSO and creating an Azure AD super admin user, you can disable the default superadmin account from the Users page.
        </p>
        <a
          href="/users"
          className="inline-flex items-center text-blue-600 hover:text-blue-700"
        >
          Go to User Management →
        </a>
      </div>
    </div>
  );
}
